/**
 * diffuserPhysics.ts — Central physics library for disc & tube diffusers
 *
 * Handles BOTH point-source (disc/dome) and line-source (tube/strip) diffusers
 * with type-correct plume scaling, coverage geometry, pore count and bubble
 * physics. All downstream calculations (PDF reports, bed analysis, recommendation
 * engines) should call this library to ensure consistent results.
 *
 * Literature (point source):
 *   Fritz, W. (1935). Bubble detachment diameter at orifice.
 *   Clift, R. et al. (1978). Bubbles, drops and particles.
 *   Loubière & Hébrard (2003). Dynamic correction to bubble size.
 *   Seol, D.G. et al. (2007). PIV measurements of bubble plume (point source).
 *   Stenstrom & Rosso (2008). SOTE from bubble diameter.
 *
 * Literature (line source / tube diffuser):
 *   Wüest, Brooks & Imboden (1992). Bubble plume modeling for lake restoration.
 *     - Covers BOTH point and line plumes; defines B0_line = B0_total / L
 *     - Centreline velocity for line plume: U_line = c × (B0_line)^(1/3)
 *   McGinnis, D.F. & Little, J.C. (2002). Discrete-bubble model for line diffusers.
 *   Asaeda & Imberger (1993). Bubble plumes in linearly stratified environments.
 *   Brevis & García (2011). Buoyant jets and plumes from line sources.
 *   Singleton & Little (2006). Hypolimnetic aeration design (line vs point).
 *
 * Coverage geometry:
 *   - Disc (point): axisymmetric cone -> circular bed footprint
 *   - Tube (line): planar curtain -> rectangular footprint (L × 2r)
 *
 * Bed shear at impingement zone:
 *   - tau = rho_w × C_D × U_bed^2
 *   - C_D = 0.01 (van Rijn 1984)
 */

const G = 9.81;
const P_ATM = 101_325;
const FRAME_SIZE_M = 2.0;
const C_D_BED = 0.01;

// ─── Diffuser geometry definition ────────────────────────────────────────────
export type DiffuserShape = 'disc' | 'tube';

export interface DiscParams {
  diamMm: number;          // disc / dome outer diameter
  poreDensityPerMm2: number;  // pores per mm^2 of disc area (0.35 EPDM, 0.4 PTFE, 0.1 ceramic)
}

export interface TubeParams {
  tubeLengthM: number;     // length of each tube (max FRAME_SIZE = 2.0 m)
  tubeCount: number;       // number of tubes per module (1-4 typical)
  tubeDiamMm: number;      // tube outer diameter (50-100 mm typical)
  pattern: 'perimeter' | 'parallel' | 'cross' | 'centre_radial';
  centreOffsetM: number;   // offset of parallel tubes from frame centre (0 = centre)
  porePerCm: number;       // pore density along tube length (6-12 pores/cm)
}

export interface DiffuserSpec {
  type: string;            // 'membrane_disc_epdm' | 'membrane_disc_ptfe' | 'ceramic_dome' | 'hdpe_porous_tube' | 'epdm_porous_tube'
  shape: DiffuserShape;
  disc?: DiscParams;
  tube?: TubeParams;
  // Common
  count: number;           // diffusers (discs or tubes) per module
  airflowPerUnitNm3h: number;  // air flow per disc OR per tube [Nm³/h]
  poreSizeUm: number;
  mountingHeightM: number;
}

// ─── Build a DiffuserSpec from a ModSpec (legacy compatibility) ──────────────
export interface LegacyModSpec {
  diffuserType: string;
  diffuserDiamMm: number;
  diffuserCount: number;
  poreSizeUm: number;
  airflowPerDiffNm3h: number;
  mountingHeightM: number;
  // Tube-specific (optional, fall back to sensible defaults)
  tubeLengthM?: number;
  tubeCount?: number;
  tubeDiamMm?: number;
  tubePattern?: TubeParams['pattern'];
  tubeCentreOffsetM?: number;
  porePerCm?: number;
}

export function specFromLegacy(m: LegacyModSpec): DiffuserSpec {
  if (m.diffuserType === 'hdpe_porous_tube' || m.diffuserType === 'epdm_porous_tube') {
    const tubeCount = m.tubeCount ?? Math.min(4, m.diffuserCount);
    const tubeLen   = Math.min(FRAME_SIZE_M, m.tubeLengthM ?? 1.8);
    return {
      type: m.diffuserType, shape: 'tube',
      count: tubeCount,
      airflowPerUnitNm3h: m.airflowPerDiffNm3h,
      poreSizeUm: m.poreSizeUm,
      mountingHeightM: m.mountingHeightM,
      tube: {
        tubeLengthM: tubeLen, tubeCount,
        tubeDiamMm: m.tubeDiamMm ?? 75,
        pattern: m.tubePattern ?? 'perimeter',
        centreOffsetM: m.tubeCentreOffsetM ?? 0.5,
        porePerCm: m.porePerCm ?? 8,
      },
    };
  }
  // disc / dome
  const density = m.diffuserType === 'membrane_disc_ptfe' ? 0.40
                : m.diffuserType === 'ceramic_dome'       ? 0.10
                : 0.35; // EPDM default
  return {
    type: m.diffuserType, shape: 'disc',
    count: m.diffuserCount,
    airflowPerUnitNm3h: m.airflowPerDiffNm3h,
    poreSizeUm: m.poreSizeUm,
    mountingHeightM: m.mountingHeightM,
    disc: { diamMm: m.diffuserDiamMm, poreDensityPerMm2: density },
  };
}

// ─── Pore count ──────────────────────────────────────────────────────────────
/** Total active pores per single diffuser unit (one disc OR one tube). */
export function poresPerUnit(spec: DiffuserSpec): number {
  if (spec.shape === 'tube' && spec.tube) {
    return Math.round(spec.tube.porePerCm * spec.tube.tubeLengthM * 100);
  }
  if (spec.shape === 'disc' && spec.disc) {
    const r = spec.disc.diamMm / 2;
    return Math.round(spec.disc.poreDensityPerMm2 * Math.PI * r * r);
  }
  return 0;
}

/** Total pores per module (n units × per-unit pores). */
export function poresPerModule(spec: DiffuserSpec): number {
  return poresPerUnit(spec) * spec.count;
}

/** Total airflow per module [Nm3/h]. */
export function qPerModuleNm3h(spec: DiffuserSpec): number {
  return spec.airflowPerUnitNm3h * spec.count;
}

// ─── Water properties (re-exported for convenience) ──────────────────────────
export function rhoW(T: number): number {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
}
export function muW(T: number): number { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
export function nuW(T: number): number { return muW(T) / rhoW(T); }
export function sigmaW(T: number): number { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
export function doSat(T: number): number { return 468 / (31.6 + T); }

// ─── Bubble formation (Fritz + Loubière) — TYPE-INDEPENDENT ──────────────────
export function bubbleDiameterMm(spec: DiffuserSpec, T: number): number {
  const d_p = spec.poreSizeUm / 1e6;
  const d_st = Math.pow((6 * sigmaW(T) * d_p) / (rhoW(T) * G), 1/3);
  const totalPores = poresPerModule(spec);
  const q_p = (qPerModuleNm3h(spec) / 3600) / Math.max(1, totalPores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}

/** Clift (1978) rise velocity with kinematic-viscosity correction. */
export function bubbleRiseVelMs(d_mm: number, T: number): number {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(nuW(20) / nuW(T), 0.7);
}

/** Stenstrom & Rosso (2008) standard oxygen transfer efficiency per metre. */
export function sotePerMetre(d_b_mm: number): number {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_b_mm)));
}

// ─── Plume geometry — TYPE-DEPENDENT ─────────────────────────────────────────

/**
 * Bed-level horizontal influence radius PER DIFFUSER UNIT.
 *   - Disc: r = max(r_diff, 0.35 × H_eff)  [Seol 2007]
 *   - Tube: r = max(tubeDiam/2, 0.10 × H_eff)  [Wüest 1992 line-plume half-angle]
 */
export function bedInfluenceRadiusM(spec: DiffuserSpec, H_eff: number): number {
  if (spec.shape === 'tube' && spec.tube) {
    return Math.max(spec.tube.tubeDiamMm / 2000, 0.10 * H_eff);
  }
  if (spec.shape === 'disc' && spec.disc) {
    return Math.max(spec.disc.diamMm / 2000, 0.35 * H_eff);
  }
  return 0.35 * H_eff;
}

/**
 * Surface plume radius PER DIFFUSER UNIT (point source) or half-width (line source).
 *   - Disc: r_surface = r_diff + 0.1 × H  [5.7° cone half-angle, Seol 2007]
 *   - Tube: half-width = r_tube + 0.1 × H  [planar curtain half-angle, Wüest 1992]
 */
export function surfaceRadiusM(spec: DiffuserSpec, H_eff: number): number {
  if (spec.shape === 'tube' && spec.tube) {
    return spec.tube.tubeDiamMm / 2000 + 0.1 * H_eff;
  }
  if (spec.shape === 'disc' && spec.disc) {
    return spec.disc.diamMm / 2000 + 0.1 * H_eff;
  }
  return 0.1 * H_eff;
}

/**
 * Bed coverage area PER MODULE [m²] — type-dependent geometry.
 *   - Disc: π × r² (circular, summed over n discs but capped at frame footprint + edge)
 *   - Tube: (L_total + 2r) × (2r) (rectangular curtain footprint)
 */
export function moduleBedCoverageAreaM2(spec: DiffuserSpec, H_eff: number): number {
  const r = bedInfluenceRadiusM(spec, H_eff);
  if (spec.shape === 'tube' && spec.tube) {
    const L_total = spec.tube.tubeLengthM * spec.tube.tubeCount;
    return (L_total + 2 * r) * (2 * r);
  }
  // Disc: union of n circles — approximate as outer envelope
  // For 8 discs in 2x2 m frame with corner offsets, footprint ~ (frameSize + 2r) ^ 2
  return Math.pow(FRAME_SIZE_M + 2 * r, 2);
}

/**
 * Plume-induced bed velocity at the impingement zone [m/s].
 * Type-dependent scaling:
 *   - Disc (point source): U = 1.46 × (B0 × H)^(1/3)   [Seol 2007]
 *   - Tube (line source):  U = 1.00 × (B0/L)^(1/3)     [Wüest 1992 / McGinnis 2002]
 *     where B0/L is the buoyancy flux per unit tube length
 */
export function plumeBedVelocityMs(spec: DiffuserSpec, H_eff: number): number {
  const q_module = qPerModuleNm3h(spec);
  const B0 = G * (q_module / 3600);  // m^4/s^3 total
  if (spec.shape === 'tube' && spec.tube) {
    const L_total = Math.max(0.1, spec.tube.tubeLengthM * spec.tube.tubeCount);
    const B0_line = B0 / L_total;
    return 1.00 * Math.pow(Math.max(B0_line, 1e-12), 1/3);
  }
  // disc point source
  return 1.46 * Math.pow(Math.max(B0 * H_eff, 1e-12), 1/3);
}

/** Bed shear stress [Pa] from plume bed velocity. */
export function bedShearPa(spec: DiffuserSpec, H_eff: number, T: number): number {
  const U = plumeBedVelocityMs(spec, H_eff);
  return rhoW(T) * C_D_BED * U * U;
}

/**
 * Shields parameter (dimensionless) for given sediment.
 *   theta = tau / [(rho_s - rho_w) × g × d50]
 */
export function shieldsParameter(
  spec: DiffuserSpec, H_eff: number, T: number,
  sediment: { d50_um: number; rho_s_kgm3: number },
): number {
  const tau = bedShearPa(spec, H_eff, T);
  const d50 = sediment.d50_um / 1e6;
  return tau / Math.max(1e-9, (sediment.rho_s_kgm3 - rhoW(T)) * G * d50);
}

// ─── Oxygen transfer ─────────────────────────────────────────────────────────
const RHO_AIR_STD = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20 = 9.09;

export function moduleSOTRkgh(spec: DiffuserSpec, H_eff: number, T: number): number {
  const d_b = bubbleDiameterMm(spec, T);
  const sote_m = sotePerMetre(d_b);
  return qPerModuleNm3h(spec) * RHO_AIR_STD * O2_MASS_FRAC * (sote_m * H_eff / 100);
}

export function moduleAOTRkgh(
  spec: DiffuserSpec, H_eff: number, T: number,
  alpha: number, beta: number, currentDOMgL: number,
): number {
  const sotr = moduleSOTRkgh(spec, H_eff, T);
  return sotr * alpha * beta * Math.pow(1.024, T - 20)
    * Math.max(0, doSat(T) - currentDOMgL) / DO_SAT_20;
}

export function blowerKwForModule(
  spec: DiffuserSpec, H_eff: number,
  dropKpa: number = 1.5, safetyPct: number = 10, eff: number = 0.7,
): number {
  const gamma = 1.4;
  const Q = qPerModuleNm3h(spec) / 3600;
  const P_out = P_ATM + (rhoW(20) * G * H_eff + dropKpa * 1000) * (1 + safetyPct / 100);
  const exp = (gamma - 1) / gamma;
  return (gamma / (gamma - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / eff / 1000;
}

// ─── Tube positions on frame (for visual rendering) ──────────────────────────
export interface TubeSegment { x1: number; y1: number; x2: number; y2: number }

/**
 * Generate tube line segments in module-local coordinates (0..FRAME_SIZE).
 * Used by visual representations (SVG / PDF) to draw tubes correctly.
 */
export function tubeSegments(tube: TubeParams): TubeSegment[] {
  const L = tube.tubeLengthM;
  const c = (FRAME_SIZE_M - L) / 2;          // start offset to centre tube along axis
  const o = tube.centreOffsetM;              // offset from frame centre
  const off = Math.max(0.1, Math.min(FRAME_SIZE_M / 2 - 0.05, o));

  switch (tube.pattern) {
    case 'perimeter': {
      // 4 tubes, one along each side, inset 0.1 m from edge
      const inset = 0.1;
      return [
        { x1: c, y1: inset,                   x2: c + L, y2: inset },
        { x1: c, y1: FRAME_SIZE_M - inset,    x2: c + L, y2: FRAME_SIZE_M - inset },
        { x1: inset, y1: c,                   x2: inset, y2: c + L },
        { x1: FRAME_SIZE_M - inset, y1: c,    x2: FRAME_SIZE_M - inset, y2: c + L },
      ].slice(0, tube.tubeCount);
    }
    case 'parallel': {
      const out: TubeSegment[] = [];
      if (tube.tubeCount === 1) {
        out.push({ x1: c, y1: FRAME_SIZE_M / 2, x2: c + L, y2: FRAME_SIZE_M / 2 });
      } else {
        // pairs above/below centre by off
        for (let i = 0; i < tube.tubeCount; i++) {
          const sign = (i % 2 === 0) ? -1 : 1;
          const idx = Math.floor(i / 2) + 1;
          const y = FRAME_SIZE_M / 2 + sign * off * idx;
          out.push({ x1: c, y1: y, x2: c + L, y2: y });
        }
      }
      return out;
    }
    case 'cross': {
      const mid = FRAME_SIZE_M / 2;
      return [
        { x1: c, y1: mid, x2: c + L, y2: mid },
        { x1: mid, y1: c, x2: mid, y2: c + L },
      ].slice(0, tube.tubeCount);
    }
    case 'centre_radial': {
      const mid = FRAME_SIZE_M / 2;
      const segs: TubeSegment[] = [];
      for (let i = 0; i < tube.tubeCount; i++) {
        const angle = (i / tube.tubeCount) * 2 * Math.PI;
        segs.push({
          x1: mid, y1: mid,
          x2: mid + Math.cos(angle) * L / 2, y2: mid + Math.sin(angle) * L / 2,
        });
      }
      return segs;
    }
  }
}

// ─── Diagnostics for reports / debug ─────────────────────────────────────────
export function describeShape(spec: DiffuserSpec): string {
  if (spec.shape === 'tube' && spec.tube) {
    return `${spec.tube.tubeCount} × ${spec.tube.tubeLengthM.toFixed(2)} m tubes (Ø${spec.tube.tubeDiamMm} mm, ${spec.tube.pattern}, ${spec.tube.porePerCm} pores/cm)`;
  }
  if (spec.shape === 'disc' && spec.disc) {
    return `${spec.count} × Ø${spec.disc.diamMm} mm discs (${spec.disc.poreDensityPerMm2.toFixed(2)} pores/mm²)`;
  }
  return 'unknown';
}

export const PLUME_SHAPE_LABEL: Record<DiffuserShape, string> = {
  disc: 'Point source - axisymmetric cone (Seol 2007)',
  tube: 'Line source - planar curtain (Wüest 1992)',
};
